/**
 * Per-client destination selection persisted in localStorage.
 *
 * Behavior:
 *   - If the user is admin-locked to a destination (`user.dest_id`), that wins
 *     and the UI hides the selector.
 *   - Otherwise the user picks one at runtime; the choice is stored locally so
 *     the same destination is auto-selected next time.
 *   - If the client has only 1 destination, it's auto-selected and the selector
 *     is hidden.
 */
import { useCallback, useEffect, useState } from "react";

const KEY_PREFIX = "triolo_dest_";

const storageKey = (clientId) => `${KEY_PREFIX}${clientId}`;

export function loadSelectedDest(clientId) {
  if (!clientId) return null;
  try {
    return localStorage.getItem(storageKey(clientId)) || null;
  } catch (err) {
    console.warn("loadSelectedDest: localStorage read failed", err);
    return null;
  }
}

export function saveSelectedDest(clientId, destId) {
  if (!clientId) return;
  try {
    if (!destId) localStorage.removeItem(storageKey(clientId));
    else localStorage.setItem(storageKey(clientId), destId);
  } catch (err) {
    console.warn("saveSelectedDest: localStorage write failed", err);
  }
}

/**
 * Hook used by Catalog and Cart to share a coherent destination state.
 *
 * Returns:
 *   destinations  — full list fetched from /clients/{id}/destinations
 *   selectedDest  — UUID of the currently active destination (or null)
 *   setSelectedDest(id) — change the runtime selection (no-op if locked)
 *   loading       — fetch in progress
 *   isLocked      — true if user.dest_id is set (admin-assigned)
 *   isHidden      — true if selector should be hidden (locked OR <2 destinations)
 */
export function useClientDestination(user, authAxios) {
  const [destinations, setDestinations] = useState([]);
  const [selectedDest, setSelectedDestState] = useState(null);
  const [loading, setLoading] = useState(false);

  const clientId = user?.client_id || null;
  const lockedDestId = user?.dest_id || null;

  useEffect(() => {
    if (!clientId) {
      setDestinations([]);
      setSelectedDestState(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await authAxios.get(`/clients/${clientId}/destinations`);
        if (cancelled) return;
        const list = r.data || [];
        setDestinations(list);
        // Pick: locked > stored > only-one > none
        let next = lockedDestId;
        if (!next) {
          const stored = loadSelectedDest(clientId);
          if (stored && list.some((d) => d.id === stored)) next = stored;
        }
        if (!next && list.length === 1) next = list[0].id;
        setSelectedDestState(next || null);
      } catch {
        if (!cancelled) setDestinations([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [authAxios, clientId, lockedDestId]);

  const setSelectedDest = useCallback((destId) => {
    if (lockedDestId) return; // ignore changes when admin-locked
    setSelectedDestState(destId || null);
    saveSelectedDest(clientId, destId);
  }, [clientId, lockedDestId]);

  return {
    destinations,
    selectedDest,
    setSelectedDest,
    loading,
    isLocked: !!lockedDestId,
    isHidden: !!lockedDestId || destinations.length < 2,
    // Cliente con più punti vendita e nessuna scelta: deve scegliere PRIMA di operare
    needsChoice: !loading && !lockedDestId && destinations.length > 1 && !selectedDest,
  };
}
